import { API } from '@/src/api/api'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { useEmberStore } from '@/src/features/Ember/infrastructure/useEmberStore'
import { buildCategory, buildImpact, buildKPI, buildText } from '@widgets/InsightsVideoCta/InsightsList'
import { type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { EmberChatAreaUI } from './EmberChatAreaUI'
import { isEmberSubagent } from './constants/ember-subagents'
import { useEmberThreadScopeSetup } from './hooks/useEmberThreadScopeSetup.hook'
import { useEmberToolCallHandling } from './hooks/useEmberToolCallHandling.hook'

const AVAILABLE_USER_ACTIONS = ['create_measure']
const AVAILABLE_VISUALIZATIONS = ['visualize_load', 'get_visualize_day_consumption']

interface ShapedInsight {
  id: string | number
  category: string
  createdAt: string
  impact: string
  kpi: string
  text: string
  type?: string
}

const shapeInsightForEmber = (insight: any, t: (key: string, options?: Record<string, unknown>) => string): string | undefined => {
  if (!insight) {
    return
  }

  const insightForEmber: Partial<ShapedInsight> = {}
  insightForEmber.id = insight.id
  if (insight.isCustom) {
    insightForEmber.category = insight.category
    insightForEmber.createdAt = insight.createdAt
    insightForEmber.impact = insight.impact
    insightForEmber.kpi = insight.kpi
    insightForEmber.text = insight.text
    insightForEmber.type = insight.type
  } else {
    insightForEmber.category = buildCategory(insight, t)
    insightForEmber.createdAt = insight.createdAt
    insightForEmber.impact = buildImpact(insight, t).text
    insightForEmber.kpi = buildKPI(insight)
    insightForEmber.text = buildText(insight, 'black', t).text
    // insightForEmber.type = insight.type //TODO: Clarify how to build: positive vs good, negative vs bad, etc
  }
  return JSON.stringify(insightForEmber)
}

interface IEmberChatAreaProps {
  style?: {
    paddingTop?: string
  }
  insight?: ShapedInsight
  proposedQuestions?: string[]
  instructions?: string
}

export const EmberChatArea: FC<IEmberChatAreaProps> = ({ style, insight, proposedQuestions: proposedQuestionsProp, instructions }) => {
  const { t } = useTranslation()
  const locations = useLocationsContext()

  // State that is custom to this chat UI
  const emberStore = useEmberStore()
  const proposedQuestions = emberStore.proposedQuestions
  const proposedUserAction = emberStore.proposedUserAction

  const {
    messages,
    handleSendMessage,
    updateLastMessage,
    cancelExecution,
    isLoading,
    setIsLoading,
    questionInput,
    setQuestionInput,
    thinkingExplanation,
    setThinkingExplanation,
  } = useEmberThreadScopeSetup(
    'chat',
    async (threadId, data) =>
      await API.ember.chatAgent.ask({
        requestBody: {
          ...data,
          threadId,
          insight: shapeInsightForEmber(insight, t),
          instructions,
        },
      }),
    {
      onBeforeSend: () => {
        emberStore.clearProposedQuestions()
        emberStore.setProposedUserAction(null)
      },
      onError: () => {
        useEmberStore.getState().setProposedUserAction(null)
      },
    },
    {
      onStreaming: () => setThinkingExplanation(null),
      onError: () => {
        setIsLoading(false)
        setThinkingExplanation(null)
        useEmberStore.getState().setProposedUserAction(null)
      },
      onDone: () => {
        setIsLoading(false)
        setThinkingExplanation(null)

        if (proposedUserAction) {
          emberStore.setProposedUserAction({ ...proposedUserAction, readyToShow: true })
        }
      },
    }
  )

  useEmberToolCallHandling({
    onToolCallCreated: event => {
      if (isEmberSubagent(event.toolName)) {
        setThinkingExplanation(t(`emberChat.thinking_next_steps`))
      } else {
        setThinkingExplanation(t(`emberChat.${event.toolName}`))
      }
    },
    onToolCallDelta: event => {
      if (AVAILABLE_USER_ACTIONS.includes(event.toolName)) {
        emberStore.setProposedUserAction({
          action: {
            type: event.toolName,
            functionArguments: event.data,
            locationId: locations.currentLocation?.locationPgId,
          },
          readyToShow: false, // the proposed user action should be shown after the current message is done
        })
      } else if (AVAILABLE_VISUALIZATIONS.includes(event.toolName)) {
        updateLastMessage({ visualization: JSON.parse(event.data) })
      }
    },
  })

  // Sync proposed questions from props to store
  useEffect(() => {
    if (proposedQuestionsProp) {
      emberStore.setProposedQuestions(proposedQuestionsProp)
    }
  }, [proposedQuestionsProp, emberStore])

  return (
    <EmberChatAreaUI
      messages={messages}
      isLoading={isLoading}
      value={questionInput}
      proposedQuestions={proposedQuestions}
      onSend={question => handleSendMessage(question)}
      onCancel={() => cancelExecution()}
      insight={insight}
      onInputChange={setQuestionInput}
      containerProps={style}
      thinkingExplanation={thinkingExplanation}
      proposedUserAction={proposedUserAction}
    />
  )
}
